import { type MouseEvent, useCallback, useState } from 'react';
import { MatchingOperator } from '../../../../../constants';
import { useNormalizedDefinitionValidation } from '../../../../../hooks/useNormalizedDefinitionValidation';
import type {
  AudienceListType,
  Group,
  GroupFieldName,
  LookalikeSegmentDefinition,
  SimilarityLevelValue,
} from '../../../../../types';
import type { DrawerState } from '../../../drawerTypes';
import { drawerType } from '../../../drawerTypes';
import { isIndexedGroupFieldName } from '../../../utils/getGroupFromPath';
import { createSegmentsFromLookalikeDefinition } from '../../drawer/components/SegmentsList/utils/createSegmentsFromLookalikeDefinition';

export type UseLookalikeSegmentHandlersParams = {
  fieldAsGroup: Group;
  groupName: string;
  groupFieldName: GroupFieldName;
  listType: AudienceListType;
  index: number;
  onToggleExpand: () => void;
  setDrawerState: (state: DrawerState) => void;
  update: (index: number, value: Group) => void;
  lookalikeDefinitionsMap: Map<string, LookalikeSegmentDefinition>;
  onShrinkSection: () => void;
  onExpandSection: () => void;
};

export type UseLookalikeSegmentHandlersReturn = {
  showClearModal: boolean;
  showLookalikeModal: boolean;
  selectedDefinitionForModal: LookalikeSegmentDefinition | null;
  handleSectionToggle: () => void;
  handleEditClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleRemoveDefinition: (definitionId: string) => void;
  handleEditDefinition: (definitionId: string) => void;
  handleModalConfirm: (selection: { similarityLevel: SimilarityLevelValue; includeSeedSegment: boolean }) => void;
  handleModalClose: () => void;
  handleClearAll: () => void;
  handleClearButtonClick: (event: MouseEvent<HTMLButtonElement>) => void;
  handleClearConfirm: () => void;
  handleClearCancel: () => void;
};

export const useLookalikeSegmentHandlers = ({
  fieldAsGroup,
  groupName,
  groupFieldName,
  listType,
  index,
  onToggleExpand,
  setDrawerState,
  update,
  lookalikeDefinitionsMap,
  onShrinkSection,
  onExpandSection,
}: UseLookalikeSegmentHandlersParams): UseLookalikeSegmentHandlersReturn => {
  const [showClearModal, setShowClearModal] = useState(false);
  const [showLookalikeModal, setShowLookalikeModal] = useState(false);
  const [selectedDefinitionForModal, setSelectedDefinitionForModal] = useState<LookalikeSegmentDefinition | null>(null);
  const { withValidation } = useNormalizedDefinitionValidation();

  const updateWithValidation = useCallback(withValidation(update), []);

  const handleSectionToggle = useCallback(() => {
    onToggleExpand();
  }, [onToggleExpand]);

  const handleEditClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (!isIndexedGroupFieldName(groupFieldName)) {
        return;
      }

      setDrawerState({
        isOpen: true,
        type: drawerType.LOOKALIKE,
        title: groupName,
        marketContext: {
          region: fieldAsGroup.region ?? null,
          countries: fieldAsGroup.countries ?? [],
        },
        fieldPath: groupFieldName,
        updater: value => updateWithValidation(index, value),
        listType,
        onShrinkSection,
        onExpandSection,
      });
    },
    [
      setDrawerState,
      groupName,
      groupFieldName,
      updateWithValidation,
      index,
      listType,
      onShrinkSection,
      onExpandSection,
      fieldAsGroup.region,
      fieldAsGroup.countries,
    ]
  );

  const handleRemoveDefinition = useCallback(
    (definitionId: string) => {
      const updatedGroup = { ...fieldAsGroup };

      if (updatedGroup.lookalike?.segments) {
        updatedGroup.lookalike.segments = updatedGroup.lookalike.segments.filter(
          segment => segment.lookalike_definition_id !== definitionId
        );
      }

      updateWithValidation(index, updatedGroup);

      if ((updatedGroup.lookalike?.segments?.length ?? 0) === 0) {
        onShrinkSection();
      }
    },
    [fieldAsGroup, updateWithValidation, index, onShrinkSection]
  );

  const handleEditDefinition = useCallback(
    (definitionId: string) => {
      const definition = lookalikeDefinitionsMap.get(definitionId);
      if (definition) {
        setSelectedDefinitionForModal(definition);
        setShowLookalikeModal(true);
      }
    },
    [lookalikeDefinitionsMap]
  );

  const handleModalConfirm = useCallback(
    (selection: { similarityLevel: SimilarityLevelValue; includeSeedSegment: boolean }) => {
      if (!selectedDefinitionForModal) return;

      const newSegments = createSegmentsFromLookalikeDefinition(selectedDefinitionForModal, selection);
      const definitionId = selectedDefinitionForModal.id;

      const updatedGroup = { ...fieldAsGroup };

      if (!updatedGroup.lookalike) {
        updatedGroup.lookalike = { segments: [], operator: MatchingOperator.UNION };
      }

      if (updatedGroup.lookalike?.segments) {
        // Find the index of the first segment with this definition ID to preserve order
        const firstSegmentIndex = updatedGroup.lookalike.segments.findIndex(
          segment => segment.lookalike_definition_id === definitionId
        );

        if (firstSegmentIndex !== -1) {
          // Edit mode: Replace segments for this definition in place to preserve order
          // Remove all segments for this definition
          const filteredSegments = updatedGroup.lookalike.segments.filter(
            segment => segment.lookalike_definition_id !== definitionId
          );

          // Insert new segments at the original position
          filteredSegments.splice(firstSegmentIndex, 0, ...newSegments);
          updatedGroup.lookalike.segments = filteredSegments;
        } else {
          updatedGroup.lookalike.segments.push(...newSegments);
        }
      }

      updateWithValidation(index, updatedGroup);
      setSelectedDefinitionForModal(null);
      setShowLookalikeModal(false);
    },
    [selectedDefinitionForModal, fieldAsGroup, updateWithValidation, index]
  );

  const handleModalClose = useCallback(() => {
    setSelectedDefinitionForModal(null);
    setShowLookalikeModal(false);
  }, []);

  const handleClearAll = useCallback(() => {
    const updatedGroup = { ...fieldAsGroup };

    if (updatedGroup.lookalike) {
      updatedGroup.lookalike.segments = [];
    }

    updateWithValidation(index, updatedGroup);
    onShrinkSection();
  }, [fieldAsGroup, updateWithValidation, index, onShrinkSection]);

  const handleClearButtonClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setShowClearModal(true);
  }, []);

  const handleClearConfirm = useCallback(() => {
    handleClearAll();
    setShowClearModal(false);
  }, [handleClearAll]);

  const handleClearCancel = useCallback(() => {
    setShowClearModal(false);
  }, []);

  return {
    showClearModal,
    showLookalikeModal,
    selectedDefinitionForModal,
    handleSectionToggle,
    handleEditClick,
    handleRemoveDefinition,
    handleEditDefinition,
    handleModalConfirm,
    handleModalClose,
    handleClearAll,
    handleClearButtonClick,
    handleClearConfirm,
    handleClearCancel,
  };
};
